import {Box, Stack, TextField, Typography} from '@wellstone-solutions/web';
import {useNoteForm} from '../../../NoteForm';
import {
  billableUnits,
  durationMinutes,
  isEndInFuture,
  unitsLabel,
} from '../../rules';
import type {ContactNoteAction, ContactNoteState} from '../../state';

const endTimeError = (state: ContactNoteState, minutes: number | null) => {
  if (!state.startTime || !state.endTime) {
    return undefined;
  }
  if (minutes === null) {
    return 'End must be after start';
  }
  if (isEndInFuture(state)) {
    return "End can't be later than now";
  }
  return undefined;
};

// Centred on the 56px inputs, not the row, so an error below End doesn't move it
const Readout = ({label, value}: {label: string; value: string}) => (
  <Box
    sx={{
      minHeight: 56,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={700}>
      {value}
    </Typography>
  </Box>
);

export const TimeFields = () => {
  const {state, dispatch} = useNoteForm<ContactNoteState, ContactNoteAction>();
  const minutes = durationMinutes(state.startTime, state.endTime);
  const endError = endTimeError(state, minutes);

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        spacing={3}
        useFlexGap
        flexWrap="wrap"
        alignItems="flex-start">
        <TextField
          required
          type="time"
          label="Start time"
          value={state.startTime}
          InputLabelProps={{shrink: true}}
          onChange={(event) =>
            dispatch({
              type: 'set',
              field: 'startTime',
              value: event.target.value,
            })
          }
        />
        <TextField
          required
          type="time"
          label="End time"
          value={state.endTime}
          error={!!endError}
          helperText={endError}
          InputLabelProps={{shrink: true}}
          onChange={(event) =>
            dispatch({type: 'set', field: 'endTime', value: event.target.value})
          }
        />
        <Readout
          label="Minutes"
          value={minutes === null ? '-' : `${minutes} min`}
        />
        <Readout
          label="Billable units (15 min)"
          value={minutes === null ? '-' : unitsLabel(billableUnits(minutes))}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Billed in 15-minute units, so start and end times are required. Minutes
        and units are calculated from them.
      </Typography>
    </Stack>
  );
};
